import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormTypes, useFormSearch } from '../../../hooks/useApi';
import DataTable from '../../components/DataTable';
import FormCard from '../../components/FormCard';
import { useI18n } from '../../context/I18nContext';
import { useAuth } from '../../context/AuthContext';

/**
 * Form Search Page
 * Search and list all form applications
 */
const FormSearch = () => {
    const navigate = useNavigate();
    const { t, getText, language } = useI18n(['common', 'search']);
    const { user } = useAuth();

    const paginationStorageKey = 'formSearch.pagination';
    const getInitialPagination = () => {
        if (typeof window === 'undefined') return { page: 1, limit: 10 };
        try {
            const raw = sessionStorage.getItem(paginationStorageKey);
            if (!raw) return { page: 1, limit: 10 };
            const parsed = JSON.parse(raw);
            const page = Number(parsed?.page) || 1;
            const limit = Number(parsed?.limit) || 10;
            return { page, limit };
        } catch {
            return { page: 1, limit: 10 };
        }
    };

    const [pagination, setPagination] = useState(() => getInitialPagination());
    const [mobileForms, setMobileForms] = useState([]);
    const [isMobile, setIsMobile] = useState(false);
    const [isFetchingNext, setIsFetchingNext] = useState(false);
    const mobileEndRef = useRef(null);
    const pendingScrollRestoreRef = useRef({ pending: false, top: 0 });
    
    // Search parameters
    const initialSearchParams = {
        formId: '',
        status: '',
        startDate: '',
        endDate: ''
    };
    const [searchParams, setSearchParams] = useState(initialSearchParams);

    // Parameters used to trigger search (updated only after clicking search)
    const [activeSearchParams, setActiveSearchParams] = useState(() => ({
        ...initialSearchParams,
        page: pagination.page,
        limit: pagination.limit
    }));

    // React Query hooks
    const { data: formTypesResult } = useFormTypes(language);
    const formTypes = formTypesResult?.data || [];

    const { 
        data: searchResult, 
        isLoading: loading, 
        isFetching,
        error: queryError 
    } = useFormSearch(activeSearchParams);

    // API 回傳 { data: { forms, pagination } }
    const forms = searchResult?.data?.forms || [];
    const paginationData = searchResult?.data?.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 };
    const error = queryError?.message;

    const handleSearch = () => {
        setActiveSearchParams({
            ...searchParams,
            page: 1,
            limit: pagination.limit
        });
        setPagination(prev => ({ ...prev, page: 1 }));
        setMobileForms([]);
    };

    const handleReset = () => {
        const resetParams = {
            formId: '',
            status: '',
            startDate: '',
            endDate: ''
        };
        setSearchParams(resetParams);
        setActiveSearchParams({
            ...resetParams,
            page: 1,
            limit: pagination.limit
        });
        setPagination(prev => ({ ...prev, page: 1 }));
        setMobileForms([]);
    };

    const handlePageChange = (page) => {
        if (typeof window !== 'undefined') {
            pendingScrollRestoreRef.current = { pending: true, top: window.scrollY };
        }
        setPagination(prev => ({ ...prev, page }));
        setActiveSearchParams(prev => ({ ...prev, page }));
    };

    const onPageSizeChange = (limit) => {
        if (typeof window !== 'undefined') {
            pendingScrollRestoreRef.current = { pending: true, top: window.scrollY };
        }
        setPagination({ page: 1, limit });
        setActiveSearchParams(prev => ({ ...prev, page: 1, limit }));
        setMobileForms([]);
    };

    const renderTruncate = useCallback((content, maxWidthClass = 'max-w-[220px]', titleText) => {
        const text = titleText ?? (typeof content === 'string' ? content : '') ?? '-';
        return (
            <span className={`block ${maxWidthClass} truncate`} title={text || undefined}>
                {content || '-'}
            </span>
        );
    }, []);

    const getStatusText = (status) => {
        return t(`status.${status}`, { ns: 'common' }) || status;
    };

    const getStatusColor = (status) => {
        const colorMap = {
            draft: 'text-gray-600',
            pending: 'text-yellow-600',
            approved: 'text-green-600',
            rejected: 'text-red-600',
            returned: 'text-orange-600',
            cancelled: 'text-gray-500'
        };
        return colorMap[status] || 'text-gray-600';
    };

    const getStatusBadgeClass = (status) => {
        const map = {
            draft: 'text-gray-700 bg-gray-100 border border-gray-200',
            pending: 'text-yellow-700 bg-yellow-100 border border-yellow-200',
            approved: 'text-green-700 bg-green-100 border border-green-200',
            rejected: 'text-red-700 bg-red-100 border border-red-200',
            returned: 'text-orange-700 bg-orange-100 border border-orange-200',
            cancelled: 'text-gray-700 bg-gray-100 border border-gray-200'
        };
        return map[status] || 'text-gray-700 bg-gray-100 border border-gray-200';
    };

    const formatDate = useCallback((date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('zh-TW', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    }, []);

    const formatFormTypeName = useCallback((formId) => {
        const formType = formTypes.find(ft => ft.id === formId);
        return formType ? getText(formType.name) : formId;
    }, [formTypes, getText]);

    const columns = useMemo(() => [
        {
            accessorKey: 'formId',
            header: t('formType', { ns: 'search' }),
            size: 180,
            cell: info => {
                const type = formatFormTypeName(info.getValue());
                return renderTruncate(type || '-', 'max-w-[250px]', type || '-');
            },
            meta: { filterable: true, filterLabel: t('formType', { ns: 'search' }) },
            filterFn: 'multiSelect'
        },
        {
            accessorKey: 'createdAt',
            header: t('applicationDate', { ns: 'search' }),
            size: 130,
            cell: info => formatDate(info.getValue())
        },
        {
            accessorKey: 'applicant',
            header: t('applicant', { ns: 'search' }),
            size: 140,
            cell: info => {
                const applicant = info.getValue();
                const name = applicant?.displayName || applicant?.name || '-';
                return renderTruncate(name, 'max-w-[140px]', name);
            }
        },
        {
            id: 'applicantDepartment',
            accessorFn: row => row.applicant?.department,
            header: t('department', { ns: 'search' }),     
            size: 110,
            cell: info => renderTruncate(info.getValue() || '-', 'max-w-[110px]', info.getValue() || '-')
        },
        {
            accessorKey: 'status',
            header: t('status', { ns: 'search' }),
            size: 110,
            cell: info => {
                const statusText = getStatusText(info.getValue());
                const status = (
                    <span className={`font-medium ${getStatusColor(info.getValue())}`}>
                        {statusText}
                    </span>
                );
                return renderTruncate(status, 'max-w-[150px]', statusText);
            },
            meta: { filterable: true, filterLabel: t('status', { ns: 'search' }) },
            filterFn: 'multiSelect'
        },
        {
            id: 'currentStep',
            accessorFn: row => {
                const currentStep = row.approvalFlow?.find(step => step.stepId === row.currentStepId);
                if (!currentStep) return '-';
                const stepName = currentStep.stepName;
                if (stepName && typeof stepName === 'object') {
                    return getText(stepName);
                }
                return stepName || '-';
            },
            header: t('currentStep', { ns: 'search' }),
            size: 140,
            cell: info => renderTruncate(info.getValue() || '-', 'max-w-[180px]', info.getValue() || '-'),
            meta: { filterable: true, filterLabel: t('currentStep', { ns: 'search' }) },
            filterFn: 'multiSelect'
        },
        {
            id: 'currentApproverName',
            accessorFn: row => Array.isArray(row.currentApprover) && row.currentApprover.length > 0
                ? row.currentApprover.map(app => app.displayName || app.name).join(', ')
                : row.currentApprover?.displayName || '-',
            header: t('currentApprover', { ns: 'search' }),
            size: 140,
            cell: (info) => renderTruncate(info.getValue() || '-', 'max-w-[180px]', info.getValue() || '-'),
            meta: { filterable: true, filterLabel: t('currentApprover', { ns: 'search' }) },
            filterFn: 'multiSelect'
        },
        {
            header: t('actions', { ns: 'common' }),
            size: 120,
            cell: info => (
                <div className="flex space-x-2">
                    <button
                        onClick={() => navigate(`/forms/view/${info.row.original.applicationId}`)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                        {t('view', { ns: 'common' })}
                    </button>
                    {/* Only user can edit their own draft/returned forms */}
                    {(info.row.original.status === 'draft' || info.row.original.status === 'returned') && info.row.original.applicant?.userId === user?.id && (
                        <button
                            onClick={() => navigate(`/forms/edit/${info.row.original.applicationId}`)}
                            className="text-green-600 hover:text-green-800 text-sm"
                        >
                            {t('edit', { ns: 'common' })}
                        </button>
                    )}
                </div>
            )
        },
        {
            accessorKey: 'applicationId',
            header: t('requestNumber', { ns: 'search' }),
            size: 250,
            cell: info => {
                const value = info.getValue();
                const btn = (
                    <button
                        onClick={() => navigate(`/forms/view/${value}`)}
                        className="text-blue-600 hover:underline font-medium block text-left truncate"
                        title={value}
                    >
                        {value}
                    </button>
                );
                return renderTruncate(btn, 'max-w-[350px]', value);
            }
        }
    ], [formatDate, formatFormTypeName, formTypes, getStatusColor, getStatusText, navigate, t, getText, user?.id, renderTruncate, language]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        sessionStorage.setItem(paginationStorageKey, JSON.stringify({
            page: pagination.page,
            limit: pagination.limit
        }));
    }, [pagination.page, pagination.limit, paginationStorageKey]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 1024);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!isMobile) return;

        if (pagination.page === 1) {
            setMobileForms(forms);
        } else if (forms.length > 0) {
            setMobileForms(prev => {
                const existingIds = new Set(prev.map(item => item.applicationId));
                const next = [...prev];
                forms.forEach(item => {
                    if (!existingIds.has(item.applicationId)) {
                        next.push(item);
                    }
                });
                return next;
            });
        }
    }, [forms, pagination.page, isMobile]);

    useEffect(() => {
        if (!isFetching) {
            setIsFetchingNext(false);
        }
    }, [isFetching]);

    useEffect(() => {
        if (!pendingScrollRestoreRef.current.pending) return;
        if (isFetching) return;
        const { top } = pendingScrollRestoreRef.current;
        pendingScrollRestoreRef.current = { pending: false, top: 0 };
        if (typeof window !== 'undefined') {
            window.scrollTo({ top, behavior: 'auto' });
        }
    }, [isFetching, forms.length]);

    useEffect(() => {
        if (!isMobile) return;
        const node = mobileEndRef.current;
        if (!node) return;

        const hasMore = paginationData.page < paginationData.totalPages;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && hasMore && !isFetching && !isFetchingNext) {
                    setIsFetchingNext(true);
                    handlePageChange(pagination.page + 1);
                }
            },
            { rootMargin: '200px' }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [isMobile, pagination.page, paginationData.page, paginationData.totalPages, handlePageChange, isFetching, isFetchingNext]);

    return (
        <div className="container mx-auto px-4 py-8 max-w-7xl">
            {/* Page Title */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">{t('title', { ns: 'search' })}</h1>
                <p className="text-gray-600 mt-1">{t('description', { ns: 'search' })}</p>
            </div>

            {/* Search Area */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('searchCriteria', { ns: 'search' })}</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    {/* Form Type */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            {t('formType', { ns: 'search' })}
                        </label>
                        <select
                            value={searchParams.formId}
                            onChange={(e) => setSearchParams({ ...searchParams, formId: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                            <option value="">{t('all', { ns: 'common' })}</option>
                            {formTypes.map(type => (
                                <option key={type.id} value={type.id}>
                                    {getText(type.name)}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* 状态 */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            {t('status', { ns: 'search' })}
                        </label>
                        <select
                            value={searchParams.status}
                            onChange={(e) => setSearchParams({ ...searchParams, status: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                            <option value="">{t('all', { ns: 'common' })}</option>
                            <option value="draft">{t('status.draft', { ns: 'common' })}</option>
                            <option value="pending">{t('status.pending', { ns: 'common' })}</option>
                            <option value="approved">{t('status.approved', { ns: 'common' })}</option>
                            <option value="rejected">{t('status.rejected', { ns: 'common' })}</option>
                            <option value="returned">{t('status.returned', { ns: 'common' })}</option>
                            <option value="cancelled">{t('status.cancelled', { ns: 'common' })}</option>
                        </select>
                    </div>

                    {/* Start Date */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            {t('startDate', { ns: 'search' })}
                        </label>
                        <input
                            type="date"
                            value={searchParams.startDate}
                            onChange={(e) => setSearchParams({ ...searchParams, startDate: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>

                    {/* End Date */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            {t('endDate', { ns: 'search' })}
                        </label>
                        <input
                            type="date"
                            value={searchParams.endDate}
                            onChange={(e) => setSearchParams({ ...searchParams, endDate: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                </div>

                {/* Search Button */}
                <div className="flex space-x-3">
                    <button
                        onClick={handleSearch}
                        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
                    >
                        {t('common:search')}
                    </button>
                    <button
                        onClick={handleReset}
                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 cursor-pointer"
                    >
                        {t('common:reset')}
                    </button>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                    {error}
                </div>
            )}

            {/* Results Table */}
            <div className="bg-white rounded-lg shadow-md">
                {loading && !forms.length ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    </div>
                ) : (
                    <>
                        <div className="hidden lg:block">
                            <DataTable
                                key={language}
                                data={forms}
                                columns={columns}
                                pagination={paginationData}
                                onPageChange={handlePageChange}
                                onPageSizeChange={onPageSizeChange}
                                isLoading={isFetching}
                            />
                        </div>

                        <div className="lg:hidden space-y-3 p-4">
                            {(mobileForms.length > 0 ? mobileForms : forms).map((form) => {
                                const statusText = getStatusText(form.status);
                                const formTypeName = formatFormTypeName(form.formId);
                                const currentStepName = (() => {
                                    const currentStep = form.approvalFlow?.find(step => step.stepId === form.currentStepId);
                                    if (!currentStep) return '-';
                                    const stepName = currentStep.stepName;
                                    if (stepName && typeof stepName === 'object') {
                                        return getText(stepName);
                                    }
                                    return stepName || '-';
                                })();
                                const currentApproverName = Array.isArray(form.currentApprover) && form.currentApprover.length > 0
                                    ? form.currentApprover.map(app => app.displayName || app.name).join(', ')
                                    : form.currentApprover?.displayName || '-';

                                const actions = [
                                    {
                                        label: t('view', { ns: 'common' }),
                                        onClick: () => navigate(`/forms/view/${form.applicationId}`),
                                        variant: 'primary'
                                    }
                                ];

                                if ((form.status === 'draft' || form.status === 'returned') && form.applicant?.userId === user?.id) {
                                    actions.push({
                                        label: t('edit', { ns: 'common' }),
                                        onClick: () => navigate(`/forms/edit/${form.applicationId}`)
                                    });
                                }

                                return (
                                    <FormCard
                                        key={form.applicationId}
                                        title={form.applicationId}
                                        subtitle={formTypeName}
                                        status={statusText}
                                        statusClassName={getStatusBadgeClass(form.status)}
                                        meta={[
                                            { label: t('applicant', { ns: 'search' }), value: form.applicant?.displayName || form.applicant?.name || '-' },
                                            { label: t('department', { ns: 'search' }), value: form.applicant?.department || '-' },
                                            { label: t('currentStep', { ns: 'search' }), value: currentStepName },
                                            { label: t('currentApprover', { ns: 'search' }), value: currentApproverName },
                                            { label: t('applicationDate', { ns: 'search' }), value: formatDate(form.createdAt) },
                                        ]}
                                        actions={actions}
                                    />
                                );
                            })}
                            <div ref={mobileEndRef} className="flex items-center justify-center py-3 text-xs text-gray-500">
                                {isFetchingNext || isFetching
                                    ? 'Loading...'
                                    : ((mobileForms.length > 0 || forms.length > 0) && paginationData.page >= paginationData.totalPages ? 'End' : '')}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default FormSearch;
